import DateTimePickerRenderer from './DateTimePicker.vue';
import LocationRenderer from './Location.vue';
import VehicleLocationRenderer from './VehicleLocation.vue';
import AccordionRenderer from './Accordion.vue';
import HeroRenderer from './Hero.vue';
import TupleRenderer from './Tuple.vue';
import TupleOutOfHoursRenderer from './TupleOutOfHours.vue';
import ImageSectionRenderer from './ImageSection.vue';
import NoteSectionRenderer from './NoteSection.vue';
import RadioGroupRenderer from './RadioGroup.vue';
import RadioGroupWithNoteRenderer from './RadioGroupWithNote.vue';
import CheckBoxWithNoteRenderer from './CheckboxWithNote.vue';
import CheckboxRenderer from './Checkbox.vue';
import InvolvedPersonsRenderer from './InvolvedPersons.vue';
import UnsupportedStepRenderer from './UnsupportedStep.vue';
import ConfirmStepRenderer from './ConfirmStep.vue';
import WelcomeStepRenderer from './WelcomeStep.vue';
import MissingPropertiesRenderer from './MissingProperties.vue';
import ContactInformationRenderer from './ContactInformation.vue';
import DriverRenderer from './Driver.vue';
import VehicleRenderer from './Vehicle.vue';
import VinNumberRenderer from './VinNumber.vue';
import NumberOfPassengersRenderer from './NumberOfPassengers.vue';
import RepairLocationRenderer from './RepairLocation.vue';
import RepairFinderRenderer from './RepairFinder/RepairFinder.vue';
import HiddenRecentIncidentRenderer from './HiddenRecentIncident.vue';
import Timezone from './Timezone.vue';
import Context from './Context.vue';
import StringInputRenderer from './StringInput.vue';

import {
  JsonFormsRendererRegistryEntry,
  and,
  isBooleanControl,
  isDateTimeControl,
  isNumberControl,
  isObjectControl,
  isOneOfEnumControl,
  isStringControl,
  optionIs,
  or,
  rankWith,
  schemaMatches,
  schemaTypeIs,
  uiTypeIs,
} from '@jsonforms/core';

const HiddenRecentIncident: JsonFormsRendererRegistryEntry = {
  renderer: HiddenRecentIncidentRenderer,
  tester: rankWith(
    4,
    and(
      isBooleanControl,
      schemaMatches((schema) => 'range' in schema && 'unit' in schema)
    )
  ),
};
const StringInput: JsonFormsRendererRegistryEntry = {
  renderer: StringInputRenderer,
  tester: rankWith(1, isStringControl),
};

const HiddenTimezone: JsonFormsRendererRegistryEntry = {
  renderer: Timezone,
  tester: rankWith(
    4,
    and(
      isStringControl,
      schemaMatches((schema) => 'name' in schema && schema.name === 'timezone')
    )
  ),
};
const HiddenContext: JsonFormsRendererRegistryEntry = {
  renderer: Context,
  tester: rankWith(
    4,
    and(
      isObjectControl,
      schemaMatches((schema) => 'name' in schema && schema.name === 'context')
    )
  ),
};
const MissingPropertiesControl: JsonFormsRendererRegistryEntry = {
  renderer: MissingPropertiesRenderer,
  tester: rankWith(
    2,
    and(
      isObjectControl,
      schemaMatches((schema) => 'missingProperties' in schema && typeof schema.missingProperties === 'boolean')
    )
  ),
};

const ContactInformationControl: JsonFormsRendererRegistryEntry = {
  renderer: ContactInformationRenderer,
  tester: rankWith(
    2,
    and(
      isObjectControl,
      schemaMatches((schema) => 'contact' in schema && typeof schema.contact === 'boolean' && !!schema.contact)
    )
  ),
};

const DateTimePickerControl: JsonFormsRendererRegistryEntry = {
  renderer: DateTimePickerRenderer,
  tester: rankWith(2, isDateTimeControl),
};

const AccordionControl: JsonFormsRendererRegistryEntry = {
  renderer: AccordionRenderer,
  tester: rankWith(
    4,
    and(
      isObjectControl,
      schemaMatches((schema) => 'accordion' in schema && typeof schema.accordion === 'object')
    )
  ),
};

const LocationControl: JsonFormsRendererRegistryEntry = {
  renderer: LocationRenderer,
  tester: rankWith(
    4,
    and(
      isObjectControl,
      schemaMatches((schema) => 'location' in schema && typeof schema.location === 'boolean' && !!schema.location)
    )
  ),
};

const VehicleLocationControl: JsonFormsRendererRegistryEntry = {
  renderer: VehicleLocationRenderer,
  tester: rankWith(
    4,
    and(
      isObjectControl,
      schemaMatches(
        (schema) =>
          'vehicleLocation' in schema && typeof schema.vehicleLocation === 'boolean' && !!schema.vehicleLocation
      )
    )
  ),
};

const HeroControl: JsonFormsRendererRegistryEntry = {
  renderer: HeroRenderer,
  tester: rankWith(
    4,
    and(
      isObjectControl,
      schemaMatches((schema) => 'hero' in schema && typeof schema.hero === 'object')
    )
  ),
};

const TupleControl: JsonFormsRendererRegistryEntry = {
  renderer: TupleRenderer,
  tester: rankWith(
    10,
    and(
      uiTypeIs('Control'),
      or(isStringControl, isBooleanControl),
      schemaMatches(
        (schema) => 'options' in schema && typeof schema.options === 'object' && Array.isArray(schema.options)
      )
    )
  ),
};

const TupleOutOfHoursControl: JsonFormsRendererRegistryEntry = {
  renderer: TupleOutOfHoursRenderer,
  tester: rankWith(
    10,
    and(
      uiTypeIs('Control'),
      or(isStringControl, isBooleanControl),
      schemaMatches(
        (schema) =>
          'outOfHours' in schema &&
          typeof schema.outOfHours === 'object' &&
          'options' in schema &&
          typeof schema.options === 'object' &&
          Array.isArray(schema.options)
      )
    )
  ),
};

const DriverControl: JsonFormsRendererRegistryEntry = {
  renderer: DriverRenderer,
  tester: rankWith(
    10,
    and(
      schemaTypeIs('array'),
      schemaMatches((schema) => 'otherContact' in schema && typeof schema.otherContact === 'object')
    )
  ),
};

const VehicleControl: JsonFormsRendererRegistryEntry = {
  renderer: VehicleRenderer,
  tester: rankWith(
    10,
    and(
      uiTypeIs('Control'),
      isStringControl,
      schemaMatches((schema) => 'licensePlateSection' in schema && typeof schema.licensePlateSection === 'object')
    )
  ),
};

const VinNumberControl: JsonFormsRendererRegistryEntry = {
  renderer: VinNumberRenderer,
  tester: rankWith(
    10,
    and(
      uiTypeIs('Control'),
      isStringControl,
      schemaMatches((schema) => 'vinSection' in schema && typeof schema.vinSection === 'object')
    )
  ),
};

const NumberOfPassengersControl: JsonFormsRendererRegistryEntry = {
  renderer: NumberOfPassengersRenderer,
  tester: rankWith(
    10,
    and(
      uiTypeIs('Control'),
      isNumberControl,
      schemaMatches((schema) => 'passengersSection' in schema && typeof schema.passengersSection === 'object')
    )
  ),
};

const RepairLocationControl: JsonFormsRendererRegistryEntry = {
  renderer: RepairLocationRenderer,
  tester: rankWith(
    10,
    and(
      uiTypeIs('Control'),
      isObjectControl,
      schemaMatches(
        (schema) => 'repairLocation' in schema && typeof schema.repairLocation === 'boolean' && !!schema.repairLocation
      )
    )
  ),
};

const RepairFinderControl: JsonFormsRendererRegistryEntry = {
  renderer: RepairFinderRenderer,
  tester: rankWith(
    8,
    and(
      uiTypeIs('Control'),
      isObjectControl,
      schemaMatches((schema) => {
        return 'repairFinder' in schema && typeof schema.repairFinder === 'boolean' && !!schema.repairFinder;
      })
    )
  ),
};

const ImageSectionControl: JsonFormsRendererRegistryEntry = {
  renderer: ImageSectionRenderer,
  tester: rankWith(
    2,
    and(
      schemaTypeIs('array'),
      schemaMatches((schema) => 'imageSection' in schema && typeof schema.imageSection === 'object')
    )
  ),
};

const NoteSectionControl: JsonFormsRendererRegistryEntry = {
  renderer: NoteSectionRenderer,
  tester: rankWith(
    2,
    and(
      uiTypeIs('Control'),
      isStringControl,
      schemaMatches((schema) => 'noteOptions' in schema && typeof schema.noteOptions === 'object')
    )
  ),
};
const RadioGroupControl: JsonFormsRendererRegistryEntry = {
  renderer: RadioGroupRenderer,
  tester: rankWith(5, and(isOneOfEnumControl, optionIs('format', 'radio'))),
};

const RadioGroupWithNoteControl: JsonFormsRendererRegistryEntry = {
  renderer: RadioGroupWithNoteRenderer,
  tester: rankWith(4, and(isObjectControl, and(optionIs('withNote', true)))),
};

const CheckBoxWithNoteControl: JsonFormsRendererRegistryEntry = {
  renderer: CheckBoxWithNoteRenderer,
  tester: rankWith(5, and(isObjectControl, and(optionIs('withNote', true), and(optionIs('multiple', true))))),
};

const CheckboxControl: JsonFormsRendererRegistryEntry = {
  renderer: CheckboxRenderer,
  tester: rankWith(1, isBooleanControl),
};
const InvolvedPersonsControl: JsonFormsRendererRegistryEntry = {
  renderer: InvolvedPersonsRenderer,
  tester: rankWith(
    2,
    and(
      schemaTypeIs('array'),
      schemaMatches((schema) => 'personSection' in schema && typeof schema.personSection === 'object')
    )
  ),
};
const UnsupportedStepControl: JsonFormsRendererRegistryEntry = {
  renderer: UnsupportedStepRenderer,
  tester: rankWith(
    4,
    and(
      isObjectControl,
      schemaMatches((schema) => 'unsupportedStep' in schema && typeof schema.unsupportedStep === 'object')
    )
  ),
};
const ConfirmStepControl: JsonFormsRendererRegistryEntry = {
  renderer: ConfirmStepRenderer,
  tester: rankWith(
    4,
    and(
      isObjectControl,
      schemaMatches((schema) => 'confirmStep' in schema && typeof schema.confirmStep === 'object')
    )
  ),
};
const WelcomeStepControl: JsonFormsRendererRegistryEntry = {
  renderer: WelcomeStepRenderer,
  tester: rankWith(
    4,
    and(
      isObjectControl,
      schemaMatches((schema) => 'welcomeStep' in schema && typeof schema.welcomeStep === 'object')
    )
  ),
};
export default [
  HiddenContext,
  HiddenTimezone,
  HiddenRecentIncident,
  MissingPropertiesControl,
  ContactInformationControl,
  DateTimePickerControl,
  LocationControl,
  AccordionControl,
  HeroControl,
  TupleOutOfHoursControl,
  TupleControl,
  ImageSectionControl,
  NoteSectionControl,
  RadioGroupControl,
  RadioGroupWithNoteControl,
  CheckboxControl,
  InvolvedPersonsControl,
  UnsupportedStepControl,
  ConfirmStepControl,
  WelcomeStepControl,
  DriverControl,
  VehicleLocationControl,
  VehicleControl,
  RepairLocationControl,
  RepairFinderControl,
  CheckBoxWithNoteControl,
  VinNumberControl,
  NumberOfPassengersControl,
  StringInput,
];
